import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TrendingDown, AlertTriangle, Package, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => { if (!cancelled && ctx?.organizations?.[0]?.id) { const id = ctx.organizations[0].id; setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(orgId ? { 'X-Organization-Id': orgId } : {}) }), [token, orgId]);
  return { headers, orgId };
};

const StatCard = ({ label, value, icon: Icon, tone = 'primary' }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid={`forecast-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone === 'danger' ? 'bg-danger/10' : 'bg-primary-50'}`}><Icon className={`w-5 h-5 ${tone === 'danger' ? 'text-danger' : 'text-primary-500'}`} /></div>
      <div><div className="text-2xl font-display text-ink tabular-nums">{value}</div><div className="text-xs text-ink-muted">{label}</div></div>
    </div>
  </div>
);

const Forecast = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookback, setLookback] = useState(30);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const [dRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/forecast/demand?lookback_days=${lookback}`, { headers }),
        fetch(`${API_URL}/api/forecast/stats?lookback_days=${lookback}`, { headers }),
      ]);
      const d = dRes.ok ? await dRes.json() : { items: [] };
      setItems(d.items || []);
      setStats(sRes.ok ? await sRes.json() : null);
    } finally { setLoading(false); }
  }, [headers, lookback]);

  useEffect(() => { load(); }, [load]);

  if (!orgId && !loading) return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="forecast-no-org">No active organization. Please log in.</div>;

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="forecast-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">InventoryAI</div>
            <h1 className="font-display text-3xl text-ink">Demand Forecast</h1>
          </div>
          <select value={lookback} onChange={e => setLookback(parseInt(e.target.value))} data-testid="forecast-lookback" className="px-3 py-2 border border-surface-border rounded-md text-sm">
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <StatCard label="Tracked Items" value={stats.tracked_ingredients} icon={Package} />
            <StatCard label="At Risk" value={stats.at_risk_count} icon={AlertTriangle} tone="danger" />
            <StatCard label="Recommended Order Qty" value={stats.total_recommended_order_qty} icon={TrendingDown} />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
          : items.length === 0 ? <div className="p-10 text-center text-ink-muted" data-testid="forecast-empty">No consumption history yet. Forecasts appear once sales deplete stock (via POS / stock movements).</div>
          : (
            <table className="w-full text-sm" data-testid="forecast-table">
              <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                <tr><th className="text-left px-4 py-3">Ingredient</th><th className="text-right px-4 py-3">Avg/day</th><th className="text-right px-4 py-3">On hand</th><th className="text-right px-4 py-3">Days cover</th><th className="text-right px-4 py-3">7-day forecast</th><th className="text-right px-4 py-3">Order qty</th></tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.ingredient_id} className={`border-t border-surface-border ${i.at_risk ? 'bg-danger/5' : ''}`} data-testid={`forecast-row-${i.ingredient_id}`}>
                    <td className="px-4 py-3 font-medium text-ink">{i.name}{i.at_risk && <span className="ml-2 text-xs text-danger">at risk</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{i.avg_daily_consumption}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{i.on_hand}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{i.days_of_cover ?? '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{i.forecast_7d}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{i.recommended_order_qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Forecast;
